import { useCallback, useEffect, useMemo, useState } from 'react';
import { repositoryFactory } from '@data';
import { COURSE_EMAIL_TEMPLATE_TYPES } from '@config/emailTemplates';

/**
 * Hook for loading and managing course email templates.
 */
export function useCourseEmailTemplates(courseId) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [error, setError] = useState(null);
  const [supportedVariables, setSupportedVariables] = useState({});
  const [templateTypes, setTemplateTypes] = useState(COURSE_EMAIL_TEMPLATE_TYPES);

  const courseRepo = useMemo(() => repositoryFactory.getCourseRepository(), []);

  const loadTemplates = useCallback(async () => {
    if (!courseId) {
      setTemplates([]);
      setSupportedVariables({});
      setTemplateTypes(COURSE_EMAIL_TEMPLATE_TYPES);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await courseRepo.getCourseEmailTemplates(courseId);
      setTemplates(Array.isArray(result?.templates) ? result.templates : []);
      setSupportedVariables(result?.supportedVariables || {});
      setTemplateTypes(Array.isArray(result?.templateTypes) ? result.templateTypes : COURSE_EMAIL_TEMPLATE_TYPES);
    } catch (err) {
      setError(err.message || 'Failed to load course email templates');
    } finally {
      setLoading(false);
    }
  }, [courseId, courseRepo]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const upsertTemplate = useCallback(async (templateType, templateData) => {
    if (!courseId) {
      throw new Error('courseId is required to save email templates');
    }

    try {
      setSaving(true);
      setError(null);

      const savedTemplate = await courseRepo.upsertCourseEmailTemplate(courseId, templateType, templateData);
      setTemplates((prevTemplates) => {
        const existingIndex = prevTemplates.findIndex((template) => template.templateType === savedTemplate.templateType);
        if (existingIndex === -1) {
          return [...prevTemplates, savedTemplate];
        }

        const nextTemplates = [...prevTemplates];
        nextTemplates[existingIndex] = savedTemplate;
        return nextTemplates;
      });

      return savedTemplate;
    } catch (err) {
      setError(err.message || 'Failed to save email template');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [courseId, courseRepo]);

  const resetTemplate = useCallback(async (templateType) => {
    if (!courseId) {
      throw new Error('courseId is required to reset email templates');
    }

    try {
      setSaving(true);
      setError(null);

      const defaultTemplate = await courseRepo.resetCourseEmailTemplate(courseId, templateType);
      setTemplates((prevTemplates) => {
        const existingIndex = prevTemplates.findIndex((template) => template.templateType === defaultTemplate.templateType);
        if (existingIndex === -1) {
          return [...prevTemplates, defaultTemplate];
        }

        const nextTemplates = [...prevTemplates];
        nextTemplates[existingIndex] = defaultTemplate;
        return nextTemplates;
      });

      return defaultTemplate;
    } catch (err) {
      setError(err.message || 'Failed to reset email template');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [courseId, courseRepo]);

  const getTemplateByType = useCallback(
    (templateType) => templates.find((template) => template.templateType === templateType) || null,
    [templates]
  );

  const sendStudyReminders = useCallback(async (options = {}) => {
    if (!courseId) {
      throw new Error('courseId is required to send study reminders');
    }

    try {
      setSendingReminders(true);
      setError(null);
      return await courseRepo.sendCourseStudyReminders(courseId, options);
    } catch (err) {
      setError(err.message || 'Failed to send study reminders');
      throw err;
    } finally {
      setSendingReminders(false);
    }
  }, [courseId, courseRepo]);

  return {
    templates,
    loading,
    saving,
    sendingReminders,
    error,
    supportedVariables,
    templateTypes,
    refresh: loadTemplates,
    upsertTemplate,
    resetTemplate,
    sendStudyReminders,
    getTemplateByType,
    clearError: () => setError(null),
  };
}
